import React, { useMemo } from 'react';
import {
  Box, Button, TextField, MenuItem, Stack, IconButton, Tooltip
} from '@mui/material';
import { DataGrid } from '../common/datagrid/ResponsiveDataGrid';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import dayjs from 'dayjs';
import EmpSelectbox from '../common/form/autocomplete/EmpSelectbox';

const getYmOptions = () => {
  const options = [];
  const now = dayjs();
  for (let y = now.year(); y >= now.year() - 1; y--) {
    for (let m = 12; m >= 1; m--) {
      if (y === now.year() && m > now.month() + 1) continue;
      options.push(`${y}${String(m).padStart(2, '0')}`);
    }
  }
  return options;
};

const AttendListTab = ({
  list, loading,
  searchYm, searchUsrUuid,
  onSearchYmChange, onSearchUsrChange,
  onAdd, onEdit, onDelete,
}) => {
  const searchFormik = useMemo(() => ({
    values: { searchUsr: searchUsrUuid },
    touched: {},
    errors: {},
    setFieldValue: (_name, val) => onSearchUsrChange(val ?? ''),
  }), [searchUsrUuid, onSearchUsrChange]);

  const columns = [
    { field: 'usrNm', headerName: '직원명', width: 100 },
    { field: 'dptNm', headerName: '부서', width: 120 },
    {
      field: 'attendDt', headerName: '근무일', width: 120,
      valueFormatter: (value) => value ? dayjs(value).format('YYYY-MM-DD') : '-',
    },
    { field: 'inTm', headerName: '출근', width: 90, align: 'center', headerAlign: 'center' },
    { field: 'outTm', headerName: '퇴근', width: 90, align: 'center', headerAlign: 'center' },
    {
      field: 'workHr', headerName: '근무시간(h)', width: 110, align: 'right', headerAlign: 'right',
      valueFormatter: (value) => value ? `${parseFloat(value).toFixed(1)}h` : '-',
    },
    { field: 'rmk', headerName: '비고', flex: 1 },
    {
      field: 'actions', headerName: '', width: 90, sortable: false,
      renderCell: ({ row }) => (
        <Stack direction="row" spacing={0.5}>
          <Tooltip title="수정">
            <IconButton size="small" onClick={() => onEdit(row)}><EditIcon fontSize="small" /></IconButton>
          </Tooltip>
          <Tooltip title="삭제">
            <IconButton size="small" color="error" onClick={() => onDelete(row.attendUuid)}>
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      ),
    },
  ];

  return (
    <Box>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        sx={{ mb: 2 }}
      >
        <TextField
          select size="small" label="년월" value={searchYm}
          onChange={(e) => onSearchYmChange(e.target.value)}
          sx={{ width: { xs: '100%', sm: 140 }, minWidth: 140 }}
        >
          {getYmOptions().map((ym) => (
            <MenuItem key={ym} value={ym}>
              {ym.slice(0, 4)}년 {parseInt(ym.slice(4), 10)}월
            </MenuItem>
          ))}
        </TextField>

        <Box sx={{ width: { xs: '100%', sm: 220 }, minWidth: { sm: 220 } }}>
          <EmpSelectbox
            formik={searchFormik}
            name="searchUsr"
            label="직원 선택 (전체)"
            variant="outlined"
            margin="none"
          />
        </Box>

        <Box sx={{ flex: { xs: 0, sm: 1 } }} />

        <Button
          variant="contained" size="small" startIcon={<AddIcon />}
          onClick={onAdd}
          sx={{ alignSelf: { xs: 'flex-start', sm: 'center' } }}
        >
          출퇴근 등록
        </Button>
      </Stack>

      <DataGrid
        rows={list}
        columns={columns}
        getRowId={(row) => row.attendUuid}
        loading={loading}
        autoHeight
        density="compact"
        disableRowSelectionOnClick
        sx={{ bgcolor: 'background.paper', borderRadius: 1 }}
        initialState={{
          sorting: { sortModel: [{ field: 'attendDt', sort: 'desc' }] },
        }}
      />
    </Box>
  );
};

export default AttendListTab;
